import { ref, watch, computed } from '@vue/composition-api';
import usePersistedPerPage from '@/libs/utils/perPage';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import store from '@/store';

export default function useMailAccountsList() {
  const toast = useToast();

  const refMailAccountsTable = ref(null);

  const tableColumns = [
    { key: 'email', label: 'Mailbox', sortable: true, thClass: 'text-nowrap' },
    { key: 'name', sortable: true, thClass: 'text-nowrap' },
    { key: 'provider', sortable: true, thClass: 'text-nowrap' },
    { key: 'state', sortable: true, label: 'Status', thClass: 'text-nowrap' },
    { key: 'syncedAt', sortable: true, label: 'Last Sync', thClass: 'text-nowrap' },
    { key: 'lastError', sortable: false, label: 'Error', thClass: 'text-nowrap' },
    { key: 'createdAt', sortable: true, label: 'Linked', thClass: 'text-nowrap' },
    { key: 'actions', label: '', thClass: 'text-nowrap text-right', tdClass: 'text-right' },
  ];

  const perPage = usePersistedPerPage('mailAccountsList', 25);
  const totalAccounts = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');
  const sortBy = ref('createdAt');
  const isSortDirDesc = ref(true);
  const providerFilter = ref(null);
  const stateFilter = ref(null);

  const dataMeta = computed(() => {
    const localItemsCount = refMailAccountsTable.value ? refMailAccountsTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0), // eslint-disable-line no-mixed-operators
      to: perPage.value * (currentPage.value - 1) + localItemsCount, // eslint-disable-line no-mixed-operators
      of: totalAccounts.value,
    };
  });

  const refetchData = () => {
    if (refMailAccountsTable.value) refMailAccountsTable.value.refresh();
  };

  watch([currentPage, perPage, searchQuery, providerFilter, stateFilter], () => {
    refetchData();
  });

  const fetchMailAccounts = (ctx, callback) => {
    const sb = `${sortBy.value}:${isSortDirDesc.value ? 'desc' : 'asc'}`;

    store
      .dispatch('mailAccountStoreModule/fetchMailAccounts', {
        search: searchQuery.value || undefined,
        limit: perPage.value,
        page: currentPage.value,
        sortBy: sb,
        provider: providerFilter.value || undefined,
        state: stateFilter.value || undefined,
      })
      .then((response) => {
        const { results, totalResults } = response.data;
        callback(results);
        totalAccounts.value = totalResults;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching mail accounts',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      });
  };

  const resolveStateVariant = (state) => {
    if (state === 'connected') return 'success';
    if (state === 'connecting' || state === 'init') return 'info';
    if (state === 'authenticationError' || state === 'connectError') return 'danger';
    if (state === 'disconnected' || state === 'unset') return 'warning';
    return 'secondary';
  };

  const resolveProviderIcon = (provider) => {
    if (provider === 'gmail') return 'MailIcon';
    if (provider === 'outlook') return 'MailIcon';
    if (provider === 'imap') return 'ServerIcon';
    return 'InboxIcon';
  };

  return {
    fetchMailAccounts,
    tableColumns,
    perPage,
    currentPage,
    totalAccounts,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refMailAccountsTable,
    refetchData,

    providerFilter,
    stateFilter,

    resolveStateVariant,
    resolveProviderIcon,
  };
}
